import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@web/lib/cn';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The board's peek panel.
 *
 * Hand-written rather than pulled from a component library, because the whole
 * surface is four behaviours and every one of them is testable: trap Tab, close
 * on Escape, return focus to whatever opened it, and land initial focus on the
 * PRIMARY ACTION rather than the close button — an operator who opened this to
 * take a conversation over should be one Enter away from doing it.
 *
 * It does not animate in. A panel that slides while someone is already reaching
 * for the button inside it is a panel that gets mis-clicked.
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  initialFocusRef,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    returnFocusTo.current = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    const target = initialFocusRef?.current ?? panel?.querySelector<HTMLElement>(FOCUSABLE);
    target?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || panel === null) return;

      const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null,
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (first === undefined || last === undefined) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      // If the row that opened this has since been removed from the board,
      // focus falls to the document; putting it back on the panel's opener
      // when that opener still exists is the whole point of keeping the ref.
      if (returnFocusTo.current?.isConnected === true) returnFocusTo.current.focus();
    };
  }, [open, onClose, initialFocusRef]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      {/* Scrim: dark enough to separate, plain enough not to be a design element. */}
      <button
        type="button"
        aria-label="Close panel"
        className="absolute inset-0 bg-ground/70"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'relative flex h-full w-full flex-col border-l border-rule bg-panel',
          'md:w-[440px] lg:w-[520px]',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-rule px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold">{title}</h2>
            {subtitle !== undefined && <div className="mt-1">{subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mr-1 flex size-8 shrink-0 items-center justify-center border border-rule-strong text-ink-dim hover:bg-hover"
            aria-label="Close panel"
          >
            <span aria-hidden className="font-mono text-sm">
              ×
            </span>
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-hidden">{children}</div>

        {footer !== undefined && (
          <footer className="border-t border-rule px-4 py-3">{footer}</footer>
        )}
      </div>
    </div>
  );
}
